import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type { ProductAdvert as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { originalUrl } from "../../utils/wagtail";
import { ProductAdvertTooltip } from "./ProductAdvertTooltip";

import bannerStyles from "../../styles/composable/banner.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

/**
 * Faithful JSX transcription of the legacy sealy
 * ``components/adverts/product_advert.html`` (#33285).
 *
 * - The legacy template nested an ``include_react`` island
 *   (``ProductAdvertTooltipsTemplate``) that mapped ``tool_tips`` to
 *   ``ProductAdvertTooltip`` controls; the tooltips now render directly in
 *   this tree (the island host div and props script are gone).
 * - ``description`` is a RawHTMLBlock the legacy template rendered unescaped
 *   (``{{ self.description }}`` on an already-safe string), so it injects via
 *   ``dangerouslySetInnerHTML``.
 */
export const ProductAdvert = (props: IProps): React.JSX.Element => {
    const value = props.value;
    return (
        <section
            className={concatClassNames([
                bannerStyles.banner,
                bannerStyles.bannerShade,
                bannerStyles.bannerCutaway,
            ])}
            id="banner-response"
        >
            <figure className={bannerStyles.bannerFigure}>
                {(value.tool_tips ?? []).map((tip, i) => (
                    <ProductAdvertTooltip
                        key={i}
                        index={i + 1}
                        left={tip.left ?? null}
                        right={tip.right ?? null}
                        top={tip.top ?? null}
                        bottom={tip.bottom ?? null}
                        title={tip.title ?? ""}
                        description={tip.description ?? ""}
                    />
                ))}
                {/* Decorative image has an empty alt attr */}
                <img
                    className="img-responsive"
                    src={originalUrl(value.image)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                />
            </figure>
            <div
                className={concatClassNames([
                    bannerStyles.bannerContent,
                    bannerStyles.bannerContentTooltip,
                ])}
            >
                <h3 className={bannerStyles.bannerTitle}>{value.title}</h3>
                {/*
                 * The legacy source was
                 * ``<p class="banner__copy banner__copy--tooltip">{{ description }}</p>``
                 * with block-level RawHTML inside -- the BROWSER PARSER split
                 * the nested <p> out as an unstyled sibling, leaving the
                 * styled <p> empty. Nesting it in JSX instead survives SSR
                 * (same source markup) but the parser-split SSR DOM then
                 * mismatches React's nested expectation at hydration, and the
                 * post-rewrite DOM keeps the content INSIDE the styled <p>
                 * (visibly different from the legacy render). Reproduce the
                 * legacy parsed outcome deterministically: the styled <p>
                 * stays empty and the raw HTML renders through a
                 * layout-neutral ``display: contents`` host.
                 */}
                <p
                    className={concatClassNames([
                        bannerStyles.bannerCopy,
                        bannerStyles.bannerCopyTooltip,
                    ])}
                ></p>
                <EditorContent>
                    {(marker) => (
                        <div
                            style={{ display: "contents" }}
                            dangerouslySetInnerHTML={{
                                __html: value.description ?? "",
                            }}
                            {...marker}
                        />
                    )}
                </EditorContent>
                <p className="legal">{value.disclaimer}</p>
                {/* Static Wistia popover markup transcribed verbatim; the global widgets scanner binds it on load -- no JS here. */}
                <span
                    className={`wistia_embed wistia_async_${value.wistia_video_id ?? ""} popover=true popoverAnimateThumbnail=true popoverContent=link`}
                    style={{ display: "inline" }}
                >
                    <svg className={bannerStyles.bannerCtaIcon}>
                        <title>{t`Play Video Icon`}</title>
                        <use xlinkHref="#icon-play" />
                    </svg>
                    <a
                        className={bannerStyles.bannerCta}
                        href="#"
                        title={t`Play Video`}
                    >
                        {t`Watch the video`}
                    </a>
                </span>
            </div>
        </section>
    );
};
